import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Auto-adds a free gift product to the cart once the eligible total reaches a
 * threshold, and removes it again if the total drops back below. The gift line
 * itself is excluded from the threshold calculation.
 *
 * Config is provided by snippets/alchemy-cart-rewards.liquid via window.AlchemyCartRewards:
 *   giftVariantId {number|null}  variant id of the gift (null => feature off)
 *   giftThreshold {number}       threshold in cents
 */

const config = window.AlchemyCartRewards || {};
const GIFT_ID = config.giftVariantId ? Number(config.giftVariantId) : null;
const THRESHOLD = Number(config.giftThreshold) || 0;

const routes = (window.Theme && window.Theme.routes) || {};
const CART_URL = routes.cart_url || '/cart';
const withJs = (url) => (url && url.endsWith('.js') ? url : `${url || ''}.js`);
const ADD_URL = withJs(routes.cart_add_url || '/cart/add');
const CHANGE_URL = withJs(routes.cart_change_url || '/cart/change');

let reconciling = false;
let rerunQueued = false;

/** @returns {Promise<any>} live cart JSON */
async function getCart() {
  const res = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

/** Sum of every non-gift line, in cents. */
function eligibleTotal(cart) {
  return cart.items
    .filter((item) => item.variant_id !== GIFT_ID)
    .reduce((sum, item) => sum + item.final_line_price, 0);
}

async function postCart(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`cart request failed: ${res.status}`);
  return res.json();
}

/**
 * Tell the rest of the theme the cart changed so the drawer, cart page and
 * cart bubble refresh. We deliberately use action 'update' (not 'add') so the
 * drawer's auto-open logic is not triggered by the gift itself.
 */
function broadcast(cart) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'alchemy-gift',
      source: 'alchemy-gift',
      lines: [],
      promise: deferred.promise,
    })
  );
  // No sections attached => each cart-items-component refetches its own section,
  // which re-renders the Liquid progress bar with the new total.
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      source: 'alchemy-gift',
      items: cart.items,
      itemCount: cart.item_count,
    },
  });
}

async function reconcile() {
  if (!GIFT_ID || !THRESHOLD) return;
  if (reconciling) {
    // A cart change arrived while we were mid-check; run again afterwards so
    // the total that crossed the threshold during our fetch isn't missed.
    rerunQueued = true;
    return;
  }
  reconciling = true;
  try {
    const cart = await getCart();
    const giftLine = cart.items.find((item) => item.variant_id === GIFT_ID);
    const total = eligibleTotal(cart);

    if (total >= THRESHOLD && !giftLine) {
      await postCart(ADD_URL, { id: GIFT_ID, quantity: 1 });
      broadcast(await getCart());
    } else if (giftLine && (total < THRESHOLD || cart.item_count === giftLine.quantity)) {
      // Below threshold, or the gift is the only thing left in the cart.
      await postCart(CHANGE_URL, { id: giftLine.key, quantity: 0 });
      broadcast(await getCart());
    } else if (giftLine && giftLine.quantity > 1) {
      // Safety: never let more than one gift accumulate.
      await postCart(CHANGE_URL, { id: giftLine.key, quantity: 1 });
      broadcast(await getCart());
    }
  } catch (error) {
    console.warn('[alchemy-rewards]', error);
  } finally {
    reconciling = false;
    if (rerunQueued) {
      rerunQueued = false;
      reconcile();
    }
  }
}

// React to any cart change except the ones we cause ourselves. The theme
// dispatches this event BEFORE its /cart/add request completes, exposing the
// updated cart via event.promise — so wait for it (like cart-icon does) or the
// reconcile reads a stale total and the gift is never added.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.context === 'alchemy-gift') return;
  const settled =
    event.promise && typeof event.promise.then === 'function'
      ? event.promise.catch(() => {})
      : Promise.resolve();
  settled.then(reconcile);
});

// Catch carts already over/under threshold on load (e.g. restored sessions).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reconcile, { once: true });
} else {
  reconcile();
}
